import React, { useState } from "react";
import { Modal, Form, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import CurrencyInput from "react-currency-input-field";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import { ApiHook } from "../../hooks/apiHook";
import { reverseNumberDisplay } from "../../utils/currencyNumberDisplay";

const InvestGGFC = ({
  balance,
  show,
  handleClose,
  currency,
  contracts,
  intrestPeriod,
  bitgoWallet = "0x66d3467dc6Fc7F70937147c8e2da24791f745570",
}) => {
  const { t } = useTranslation();
  const [investData, setInvestData] = useState({
    amount: "",
    stakingPeriod: "1",
    transactionHash: "",
    userWalletAddress: "",
  });
  const [source, setSource] = useState("internal"); // new

  const conversionFactor = useSelector(
    (state) => state?.user?.conversionFactor
  );

  const defaultCurrencyJson = useSelector(
    (state) => state?.user?.loginResponse?.defaultCurrency
  );

  const defaultCurrency = defaultCurrencyJson
    ? JSON.parse(defaultCurrencyJson)
    : null;

  const [errorMessage, setErrorMessage] = useState({
    amount: null,
    stakingPeriod: null,
    transactionHash: null,
    userWalletAddress: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize the investment mutation
  const investMutation = ApiHook.CallInvestGGFC();

  // Staking period options with potential APY benefits
  // const contracts = [
  //   { value: "1", label: "1 Month", apy: "5%" },
  //   { value: "3", label: "3 Months", apy: "8%" },
  //   { value: "6", label: "6 Months", apy: "12%" },
  //   { value: "12", label: "12 Months", apy: "20%" },
  // ];

  // Generate QR code for BitGo wallet address or dummy QR code
  const generateQRCode = () => {
    if (bitgoWallet) {
      // Generate QR code for the BitGo wallet address
      // Using a simple data URL format - you can integrate with a proper QR library
      const qrData = `ethereum:${bitgoWallet}`;
      return `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
        qrData
      )}`;
    }

    // Fallback dummy QR code for non-BitGo investments
    return "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KICA8cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2ZmZiIvPgogIDxyZWN0IHg9IjIwIiB5PSIyMCIgd2lkdGg9IjE2MCIgaGVpZ2h0PSIxNjAiIGZpbGw9IiMwMDAiLz4KICA8cmVjdCB4PSI0MCIgeT0iNDAiIHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWxsPSIjZmZmIi8+CiAgPHJlY3QgeD0iNjAiIHk9IjYwIiB3aWR0aD0iODAiIGhlaWdodD0iODAiIGZpbGw9IiMwMDAiLz4KICA8cmVjdCB4PSI4MCIgeT0iODAiIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgZmlsbD0iI2ZmZiIvPgo8L3N2Zz4=";
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setInvestData((prevData) => ({
      ...prevData,
      [id]: value,
    }));
    setErrorMessage((prevData) => ({
      ...prevData,
      [id]: null,
    }));
  };

  const handleAmountChange = (value) => {
    setInvestData((prevData) => ({
      ...prevData,
      amount: value,
    }));
    if (parseFloat(value || 0) > parseFloat(balance)) {
      setErrorMessage((prevData) => ({
        ...prevData,
        amount: "Insufficient balance",
      }));
    } else {
      setErrorMessage((prevData) => ({
        ...prevData,
        amount: null,
      }));
    }
  };

  const isFormValid = () => {
    if (source === "external" && bitgoWallet) {
      return (
        investData?.amount > 0 &&
        investData?.stakingPeriod &&
        investData?.transactionHash &&
        investData?.userWalletAddress
      );
    }
    return (
      investData?.amount > 0 &&
      investData?.stakingPeriod &&
      (!balance || parseFloat(investData.amount || 0) <= parseFloat(balance))
    );
  };

  const validateForm = () => {
    let isValid = true;
    const errors = {
      amount: null,
      stakingPeriod: null,
      transactionHash: null,
      userWalletAddress: null,
    };
    if (!investData.amount || parseFloat(investData.amount) <= 0) {
      errors.amount = "Token amount must be greater than 0";
      isValid = false;
    }
    if (!investData.stakingPeriod) {
      errors.stakingPeriod = "Please select a staking period";
      isValid = false;
    }

    if (source === "external" && bitgoWallet) {
      if (!investData.transactionHash) {
        errors.transactionHash =
          "Transaction hash is required for BitGo wallet payments";
        isValid = false;
      } else if (
        !/^0x[a-fA-F0-9]{64}$/.test(investData.transactionHash.trim())
      ) {
        errors.transactionHash =
          "Please enter a valid Ethereum transaction hash";
        isValid = false;
      }
      if (!investData.userWalletAddress) {
        errors.userWalletAddress =
          "Your wallet address is required for verification";
        isValid = false;
      } else if (
        !/^0x[a-fA-F0-9]{40}$/.test(investData.userWalletAddress.trim())
      ) {
        errors.userWalletAddress =
          "Please enter a valid Ethereum wallet address";
        isValid = false;
      }
    }
    setErrorMessage(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      let convertAmount;
      if (currency?.id === defaultCurrency?.id) {
        convertAmount = reverseNumberDisplay(
          CurrencyConverter(investData?.amount, conversionFactor, 0)
        );
      } else {
        convertAmount = reverseNumberDisplay(
          CurrencyConverter(investData?.amount, conversionFactor, 1)
        );
      }
      const investmentData = {
        amount: convertAmount,
        stakingPeriod: investData.stakingPeriod,
        tokenType: "GGFC",
        source,
        ...(source === "external" && bitgoWallet
          ? {
              transactionHash: investData.transactionHash.trim(),
              userWalletAddress: investData.userWalletAddress.trim(),
              bitgoWalletAddress: bitgoWallet,
            }
          : {}),
      };
      await investMutation.mutateAsync(investmentData, {
        onSuccess: (res) => {
          if (res.status === 200) {
            setInvestData({
              amount: "",
              stakingPeriod: "1",
              transactionHash: "",
              userWalletAddress: "",
            });
            setSource("internal");
            setErrorMessage({
              amount: null,
              stakingPeriod: null,
              transactionHash: null,
              userWalletAddress: null,
            });
            handleClose();
          }
        },
        onError: (err) => console.error("Investment submission error:", err),
      });
    } catch (err) {
      // toast.error("Token investment failed. Please try again.");
      console.error("Investment error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleModalClose = () => {
    setInvestData({
      amount: "",
      stakingPeriod: "1",
      transactionHash: "",
      userWalletAddress: "",
    });
    setSource("internal");
    setErrorMessage({
      amount: null,
      stakingPeriod: null,
      transactionHash: null,
      userWalletAddress: null,
    });
    handleClose();
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        setInvestData({
          amount: "",
          stakingPeriod: "1",
          transactionHash: "",
          userWalletAddress: "",
        });
        setSource("internal");
        setErrorMessage({
          amount: null,
          stakingPeriod: null,
          transactionHash: null,
          userWalletAddress: null,
        });
        handleClose();
      }}
      size="xl"
      centered
      className="invest-modal"
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center">
          <div className="modal-icon me-3">
            <i className="fas fa-coins text-primary"></i>
          </div>
          <div>
            <h4 className="mb-0">{t("invest_ggfc")}</h4>
            <small className="text-muted">
              Stake your GGFC tokens and earn rewards
            </small>
          </div>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-4">
        {bitgoWallet && source === "external" && (
          <div
            className="alert alert-info border-0 mb-4"
            style={{ backgroundColor: "#e3f2fd" }}
          >
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle text-primary me-2"></i>
              <div>
                <strong>BitGo Wallet Investment</strong>
                <div className="small">
                  Send your GGFC tokens to the provided address and submit
                  transaction details below.
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row g-4">
          {/* Left Side - Investment Form */}
          <div className="col-lg-6">
            <div className="investment-form-container">
              <div className="form-header mb-4">
                <h5 className="mb-2">Investment Details</h5>
                <p className="text-muted small mb-0">
                  Enter your investment amount and select staking period
                </p>
              </div>

              <Form onSubmit={handleSubmit}>
                {/* Amount Input */}
                <div className="form-section mb-4">
                  <Form.Group>
                    <Form.Label className="form-label-custom">
                      {t("investment_amount")}
                      <span className="text-danger ms-1">*</span>
                    </Form.Label>
                    <div className="amount-input-wrapper">
                      <div className="input-group">
                        <span className="input-group-text bg-light border-end-0">
                          <i className="fas fa-coins text-primary"></i>
                        </span>
                        <Form.Control
                          as="select"
                          disabled
                          className="token-selector"
                        >
                          <option value="">GGFC</option>
                        </Form.Control>
                        <CurrencyInput
                          name="amount"
                          id="amount"
                          className="form-control border-start-0"
                          placeholder="0.00"
                          value={investData.amount}
                          onValueChange={(value) => handleAmountChange(value)}
                          required
                          allowNegativeValue={false}
                          decimalsLimit={4}
                        />
                      </div>
                      {errorMessage.amount && (
                        <div className="invalid-feedback d-block">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errorMessage.amount}
                        </div>
                      )}
                    </div>
                  </Form.Group>
                </div>

                {/* Staking Period */}
                {contracts && contracts.length > 0 && (
                  <div className="form-section mb-4">
                    <Form.Group>
                      <Form.Label className="form-label-custom">
                        {t("staking_period")}
                        <span className="text-danger ms-1">*</span>
                      </Form.Label>
                      <Form.Select
                        id="stakingPeriod"
                        value={investData.stakingPeriod}
                        onChange={handleChange}
                        isInvalid={errorMessage?.stakingPeriod !== null}
                        className="form-control-custom"
                      >
                        {contracts.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label} - {option.apy}
                          </option>
                        ))}
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        <i className="fas fa-exclamation-triangle me-1"></i>
                        {errorMessage.stakingPeriod}
                      </Form.Control.Feedback>
                    </Form.Group>
                  </div>
                )}

                {/* Source Selection */}
                <div className="form-section mb-4">
                  <Form.Label className="form-label-custom mb-2">
                    Investment Source
                  </Form.Label>
                  <div className="d-flex gap-3 flex-wrap">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        name="source"
                        id="source-internal"
                        value="internal"
                        checked={source === "internal"}
                        onChange={() => setSource("internal")}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="source-internal"
                      >
                        Reinvest (Internal Token)
                      </label>
                    </div>
                    {bitgoWallet && (
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="source"
                          id="source-external"
                          value="external"
                          checked={source === "external"}
                          onChange={() => {
                            setSource("external");
                            setErrorMessage((prevData) => ({
                              ...prevData,
                              amount: null,
                            }));
                          }}
                        />
                        <label
                          className="form-check-label"
                          htmlFor="source-external"
                        >
                          External (Send Tokens)
                        </label>
                      </div>
                    )}
                  </div>
                </div>

                {/* BitGo Wallet Fields */}
                {source === "external" && bitgoWallet && (
                  <div className="bitgo-fields">
                    <div className="form-divider mb-4">
                      <hr className="my-3" />
                      <div className="divider-text">
                        <small className="text-muted bg-white px-3">
                          Transaction Verification
                        </small>
                      </div>
                    </div>

                    <div className="form-section mb-4">
                      <Form.Group>
                        <Form.Label className="form-label-custom">
                          <i className="fas fa-receipt me-2 text-primary"></i>
                          Transaction Hash
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="transactionHash"
                          placeholder="0x..."
                          value={investData.transactionHash}
                          onChange={handleChange}
                          isInvalid={errorMessage?.transactionHash !== null}
                          className="form-control-custom font-monospace"
                        />
                        <Form.Control.Feedback type="invalid">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errorMessage.transactionHash}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Paste the transaction hash after sending tokens to the
                          BitGo address
                        </Form.Text>
                      </Form.Group>
                    </div>

                    <div className="form-section mb-4">
                      <Form.Group>
                        <Form.Label className="form-label-custom">
                          <i className="fas fa-wallet me-2 text-primary"></i>
                          Your Wallet Address
                          <span className="text-danger ms-1">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          id="userWalletAddress"
                          placeholder="0x..."
                          value={investData.userWalletAddress}
                          onChange={handleChange}
                          isInvalid={errorMessage?.userWalletAddress !== null}
                          className="form-control-custom font-monospace"
                        />
                        <Form.Control.Feedback type="invalid">
                          <i className="fas fa-exclamation-triangle me-1"></i>
                          {errorMessage.userWalletAddress}
                        </Form.Control.Feedback>
                        <Form.Text className="text-muted">
                          <i className="fas fa-info-circle me-1"></i>
                          Your wallet address for payment verification
                        </Form.Text>
                      </Form.Group>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="submit-section mt-4">
                  <Button
                    variant="primary"
                    type="submit"
                    disabled={
                      !isFormValid() || isSubmitting || investMutation.isLoading
                    }
                    className="btn-invest w-100"
                    size="lg"
                  >
                    {isSubmitting || investMutation.isLoading ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        Processing Investment...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-rocket me-2"></i>
                        {bitgoWallet
                          ? source === "external" ? "Verify & Invest GGFC Tokens" : "Invest GGFC Tokens"
                          : "Invest GGFC Tokens"}
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          {/* Right Side - QR Code and Summary */}
          <div className="col-lg-6">
            <div className="investment-preview">
              {/* QR Code Section */}
              <div className="qr-section mb-4">
                {source === "external" ? (
                  bitgoWallet ? (
                    <div className="bitgo-wallet-card">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-primary text-white border-0">
                          <h6 className="mb-0 d-flex align-items-center">
                            <i className="fas fa-qrcode me-2"></i>
                            BitGo Wallet Address
                          </h6>
                        </div>
                        <div className="card-body text-center p-4">
                          <div className="qr-code-container mb-3">
                            <img
                              src={generateQRCode()}
                              alt="BitGo Wallet QR Code"
                              className="img-fluid qr-image"
                            />
                          </div>
                          <div className="wallet-address-section">
                            <div className="wallet-address-label mb-2">
                              <small className="text-muted">
                                Send GGFC tokens to:
                              </small>
                            </div>
                            <div className="wallet-address-display p-3 bg-light rounded">
                              <div className="address-text font-monospace small text-break">
                                {bitgoWallet}
                              </div>
                              <button
                                type="button"
                                className="btn btn-outline-primary btn-sm mt-2"
                                onClick={() => {
                                  navigator.clipboard.writeText(bitgoWallet);
                                  toast.success("Address copied to clipboard!");
                                }}
                              >
                                <i className="fas fa-copy me-1"></i>
                                Copy Address
                              </button>
                            </div>
                          </div>
                          <div className="instructions mt-3">
                            <div className="alert alert-warning border-0 p-2">
                              <small>
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                Send tokens first, then fill in transaction
                                details on the left
                              </small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="standard-qr-card">
                      <div className="card border-0 shadow-sm">
                        <div className="card-header bg-gradient text-white border-0">
                          <h6 className="mb-0 d-flex align-items-center">
                            <i className="fas fa-qrcode me-2"></i>
                            Investment QR Code
                          </h6>
                        </div>
                        <div className="card-body text-center p-4">
                          <div className="qr-code-container mb-3">
                            <img
                              src={generateQRCode()}
                              alt="Investment QR Code"
                              className="img-fluid qr-image"
                            />
                          </div>
                          <small className="text-muted">
                            <i className="fas fa-info-circle me-1"></i>
                            Scan to complete your investment
                          </small>
                        </div>
                      </div>
                    </div>
                  )
                ) : (
                  <div className="balance-card">
                    <div className="card border-0 shadow-sm">
                      <div className="card-header bg-success text-white border-0">
                        <h6 className="mb-0 d-flex align-items-center">
                          <i className="fas fa-wallet me-2"></i>
                          Available Balance
                        </h6>
                      </div>
                      <div className="card-body p-4 text-center">
                        <div
                          className="display-6 fw-bold mb-2"
                          style={{ fontSize: "2.2rem" }}
                        >
                          {balance ?? "0"} <span className="fs-6">GGFC</span>
                        </div>
                        {investData.amount && (
                          <div className="mt-3">
                            <small className="text-muted d-block mb-1">
                              Balance After Investment
                            </small>
                            <div className="fw-semibold">
                              {balance && investData.amount
                                ? (
                                    parseFloat(balance) -
                                    parseFloat(investData.amount || 0)
                                  ).toFixed(4)
                                : "0.0000"}{" "}
                              GGFC
                            </div>
                          </div>
                        )}
                        <div className="mt-3">
                          <span
                            className="badge bg-light text-success border"
                            style={{ fontWeight: 600 }}
                          >
                            Reinvest Mode
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Investment Summary */}
              {investData.amount && investData.stakingPeriod && (
                <div className="investment-summary-card">
                  <div className="card border-0 shadow-sm">
                    <div className="card-header bg-light border-0">
                      <h6 className="mb-0 d-flex align-items-center">
                        <i className="fas fa-chart-line me-2 text-success"></i>
                        Investment Summary
                      </h6>
                    </div>
                    <div className="card-body">
                      <div className="summary-item">
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted">Amount:</span>
                          <span className="fw-bold">
                            {investData.amount} GGFC
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted">Period:</span>
                          <span className="fw-bold">
                            {
                              contracts.find(
                                (opt) => opt.value === investData.stakingPeriod
                              )?.label
                            }
                          </span>
                        </div>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <span className="text-muted">Interest Rate:</span>
                          <span className="badge bg-success fs-6">
                            {
                              contracts.find(
                                (opt) => opt.value === investData.stakingPeriod
                              )?.apy
                            }
                          </span>
                        </div>
                        <hr className="my-3" />
                        <div className="estimated-returns">
                          <div className="d-flex justify-content-between align-items-center">
                            <span className="text-muted">
                              Expected Returns
                              <small className="d-block">
                                (
                                {intrestPeriod === 1
                                  ? "Monthly"
                                  : `Every ${intrestPeriod} Months`}
                                )
                              </small>
                            </span>
                            <div className="text-end">
                              <span className="fw-bold text-success fs-5">
                                +
                                {investData.amount
                                  ? (
                                      parseFloat(investData.amount) *
                                      (parseFloat(
                                        contracts.find(
                                          (opt) =>
                                            opt.value ===
                                            investData.stakingPeriod
                                        )?.apy
                                      ) /
                                        100)
                                    ).toFixed(4)
                                  : 0}{" "}
                                GGFC
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal.Body>

      <style jsx>{`
        .invest-modal .modal-content {
          border: none;
          border-radius: 16px;
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
        }

        .modal-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #32009c 0%, #4a0eb5 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 20px;
          color: white;
        }

        .investment-form-container {
          background: #fff;
          border-radius: 12px;
          padding: 0;
        }

        .form-header h5 {
          color: #2c3e50;
          font-weight: 600;
        }

        .form-label-custom {
          font-weight: 600;
          color: #495057;
          margin-bottom: 8px;
          font-size: 14px;
        }

        .amount-input-wrapper .input-group {
          border-radius: 8px;
          overflow: hidden;
          border: 2px solid #e9ecef;
          transition: border-color 0.3s ease;
        }

        .amount-input-wrapper .input-group:focus-within {
          border-color: #32009c;
          box-shadow: 0 0 0 3px rgba(50, 0, 156, 0.1);
        }

        .token-selector {
          max-width: 80px;
          border: none;
          background: #f8f9fa;
          font-weight: 600;
          color: #32009c;
        }

        .form-control-custom {
          border: 2px solid #e9ecef;
          border-radius: 8px;
          padding: 12px 16px;
          font-size: 14px;
          transition: all 0.3s ease;
        }

        .form-control-custom:focus {
          border-color: #32009c;
          box-shadow: 0 0 0 3px rgba(50, 0, 156, 0.1);
        }

        .form-divider {
          position: relative;
          text-align: center;
        }

        .divider-text {
          position: relative;
          top: -12px;
        }

        .btn-invest {
          background: linear-gradient(135deg, #32009c 0%, #4a0eb5 100%);
          border: none;
          border-radius: 12px;
          padding: 16px 24px;
          font-weight: 600;
          letter-spacing: 0.5px;
          transition: all 0.3s ease;
          box-shadow: 0 4px 15px rgba(50, 0, 156, 0.3);
        }

        .btn-invest:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(50, 0, 156, 0.4);
        }

        .btn-invest:disabled {
          opacity: 0.6;
          transform: none;
        }

        .qr-image {
          max-width: 180px;
          border-radius: 8px;
          border: 2px solid #e9ecef;
        }

        .bitgo-wallet-card .card-header {
          background: linear-gradient(
            135deg,
            #32009c 0%,
            #4a0eb5 100%
          ) !important;
        }

        .standard-qr-card .card-header {
          background: linear-gradient(
            135deg,
            #17a2b8 0%,
            #20c997 100%
          ) !important;
        }

        .wallet-address-display {
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 8px;
        }

        .address-text {
          font-size: 12px;
          color: #495057;
          line-height: 1.4;
        }

        .investment-summary-card .summary-item {
          font-size: 14px;
        }

        .invalid-feedback {
          font-size: 13px;
          color: #dc3545;
          margin-top: 6px;
        }

        .alert {
          border-radius: 8px;
        }

        .font-monospace {
          font-family: "Monaco", "Menlo", "Ubuntu Mono", monospace !important;
        }

        .balance-card .card-header {
          background: linear-gradient(
            135deg,
            #198754 0%,
            #28a745 100%
          ) !important;
        }
        .balance-card .display-6 {
          letter-spacing: -1px;
        }

        @media (max-width: 768px) {
          .invest-modal .modal-dialog {
            margin: 10px;
          }

          .investment-form-container,
          .investment-preview {
            padding: 20px;
          }
        }
      `}</style>
    </Modal>
  );
};

export default InvestGGFC;
